import type { RigSize, WeaponClass } from '../config/character-roster';


/**
 * Which clip plays for which game state, per rig and per body style.
 *
 * Added 2026-08-25 with the KayKit Character Animations pack. Before it the
 * game had exactly two clip libraries (General + MovementBasic) whose 26 clips
 * held no attack, no block, no crouch, no cast and no roll — so
 * `character-visual.ts` mapped BLOCKING, CROUCHING and CASTING all onto
 * `Idle_A`, used `Jump_Start` as a dodge (the active dodge itself is gone
 * since 2026-10-16), and hand-authored procedural swings
 * (`combat-animations.ts`, now deleted) because there was nothing to play.
 * Every one of those substitutions has a real clip now.
 *
 * **Why fallback chains rather than a name per state.** The two rigs are not
 * symmetric and do not agree on spelling. The large rig has no crouch, no
 * sneak and no magic clips at all, spells its dodge `Dodge_Backwards` where
 * the medium rig spells it `Dodge_Backward`, and names its attacks
 * `Melee_1H_Slash`/`Melee_2H_Slam` where the medium rig uses
 * `Melee_1H_Attack_Chop`/`Melee_2H_Attack_Chop`. A single name per state would
 * mean either a table full of holes or a Bone Warden frozen mid-stride,
 * because a missed clip lookup is silent — the mixer just keeps the last pose.
 * A chain says what to do instead, in order, and always ends somewhere real.
 *
 * Pure and rig-data-only, so `animation-map.test.ts` can check every intent
 * against `animation-clips.generated.ts` — the actual contents of the shipped
 * files — instead of against a hand-copied list that can drift.
 */

/**
 * Skeletal monsters get the pack's own undead clips; everything else (players,
 * the golem-bodied Bone Warden) uses the humanoid set. A style rather than a
 * per-archetype table because it is a property of the *body*, and the two
 * skeleton archetypes that share one already share everything else.
 */
export type ClipStyle = 'humanoid' | 'undead';

export type AnimIntent =
  | 'IDLE' | 'MOVING' | 'SPRINTING'
  /**
   * Walking while facing the other way.
   *
   * Only reachable since mouse-look (camera-and-controls.md, fifth amendment)
   * split facing from travel: before it, `facing` *was* the movement direction,
   * so a body was always walking forward by construction and this clip had
   * nothing to play it. Now S walks you backwards with your guard still on
   * whatever you are backing away from, and a forward stride under that motion
   * would be a moonwalk.
   */
  | 'MOVING_BACKWARD'
  | 'CROUCH_IDLE' | 'CROUCH_MOVING'
  | 'BLOCKING' | 'PARRY' | 'CASTING'
  /**
   * Jump (camera-and-controls.md amendment, 2026-10-16) — a one-shot over
   * whatever the body is doing, never a state. The clip supplies the crouch,
   * tuck and landing; the height itself is `sim/jump.ts`'s arc applied to the
   * root, since none of the pack's jump clips leave the ground.
   */
  | 'JUMP'
  /**
   * A passive dodge landing (2026-10-16) — the short sidestep a defender plays
   * when `combat.result` says 'dodged'. Two intents so the body can step
   * either way; the visual picks one at random.
   */
  | 'SIDESTEP_LEFT' | 'SIDESTEP_RIGHT'
  | 'DEAD'
  /** Channelled item use — the consumable satchel's 1.5s drink. */
  | 'USE_ITEM'
  /** Chests, doors, cranks. */
  | 'INTERACT'
  /** A dormant skeleton getting up. */
  | 'AWAKEN'
  /**
   * Visualizing abilities (2026-08-26) — the six motions an ability press
   * plays on the caster.
   *
   * These are one-shots layered over whatever the body was doing, not states:
   * every ability except Mend is instant, so the player never leaves IDLE or
   * MOVING and the state machine has nothing to show. Before this, pressing
   * Eviscerate moved a perfectly still body not at all — the only thing that
   * happened was a ring at its feet.
   *
   * Grouped by archetype (`config/ability-vfx.ts`'s `AbilityFxKind`) rather
   * than one per ability, for the same reason the effects are: fourteen
   * bespoke motions would be fourteen clips this pack does not have.
   */
  | 'ABILITY_STRIKE' | 'ABILITY_SWEEP' | 'ABILITY_SHOUT'
  | 'ABILITY_WARD' | 'ABILITY_CAST' | 'ABILITY_STEALTH';

type IntentTable = Record<AnimIntent, readonly string[]>;

/**
 * The medium rig — all four player classes, plus the Rattler and Bonewright.
 *
 * `Idle_A` is the terminal fallback almost everywhere: it is the one clip
 * present in every set of every rig, so a chain that reaches it is guaranteed
 * to resolve rather than leave a character mid-pose.
 */
const MEDIUM_HUMANOID: IntentTable = {
  IDLE: ['Idle_A'],
  MOVING: ['Walking_A'],
  MOVING_BACKWARD: ['Walking_Backwards', 'Walking_A'],
  SPRINTING: ['Running_A'],
  CROUCH_IDLE: ['Crouching', 'Idle_A'],
  CROUCH_MOVING: ['Sneaking', 'Crouching', 'Walking_A'],
  // A held guard loop, not the one-shot raise: `Melee_Block` is the snap into
  // the stance (used for PARRY below), `Melee_Blocking` is standing in it.
  BLOCKING: ['Melee_Blocking', 'Idle_A'],
  // The short jump is the whole hop in one clip; `Jump_Start` (the takeoff
  // alone) is the fallback if a rig ships only that.
  JUMP: ['Jump_Full_Short', 'Jump_Start', 'Idle_A'],
  // The pack's sidesteps are 400ms with a half-metre of root travel the body
  // never covers server-side — short enough that it reads as a lean out of
  // the blade's path rather than a slide.
  SIDESTEP_LEFT: ['Dodge_Left', 'Dodge_Backward', 'Idle_A'],
  SIDESTEP_RIGHT: ['Dodge_Right', 'Dodge_Backward', 'Idle_A'],
  // The sharp deflect motion — a parry is a timed snap, which is exactly what
  // the block *raise* is, rather than the guard it settles into.
  PARRY: ['Melee_Block', 'Melee_Blocking', 'Idle_A'],
  CASTING: ['Ranged_Magic_Spellcasting', 'Ranged_Magic_Raise', 'Idle_A'],
  DEAD: ['Death_A'],
  USE_ITEM: ['Use_Item', 'Interact'],
  INTERACT: ['Interact', 'PickUp', 'Use_Item'],
  AWAKEN: ['Spawn_Ground', 'Idle_A'],
  // A one-handed chop: the shortest, most legible "I hit that thing on
  // purpose" in the set, and distinct from the swing chain above so an
  // ability never looks like a basic attack that happened to glow.
  ABILITY_STRIKE: ['Melee_1H_Attack_Chop', 'Melee_1H_Attack_Slice_Diagonal', 'Idle_A'],
  // The spin is the only clip in the pack that describes a circle, which is
  // exactly the shape Cleave's radius is drawn as.
  ABILITY_SWEEP: ['Melee_2H_Attack_Spin', 'Melee_2H_Attack_Spinning', 'Melee_1H_Attack_Slice_Horizontal'],
  // No shout clip exists (`Cheering` is in the emote file, which is loaded
  // separately and only on demand). The magic *raise* — arms thrown up and
  // out — is the closest honest motion for a bellow, and it is the same
  // gesture whether what comes out is a spell or a Warcry.
  ABILITY_SHOUT: ['Ranged_Magic_Raise', 'Melee_2H_Attack_Spin', 'Idle_A'],
  // The snap into guard, same clip PARRY uses: bracing and parrying are the
  // same motion, and Brace is literally named after it.
  ABILITY_WARD: ['Melee_Block', 'Melee_Blocking', 'Idle_A'],
  // The instant spells. `CASTING` above is the held pose for Mend's 1.5s
  // channel; this is the same clip played once for a spell that is already
  // out of the hand by the time the animation finishes.
  ABILITY_CAST: ['Ranged_Magic_Spellcasting', 'Ranged_Magic_Raise', 'Idle_A'],
  // Vanish. Dropping into a crouch reads as going to ground; the body dims at
  // the same time (`SHROUD`), which is what sells it.
  ABILITY_STEALTH: ['Crouching', 'Dodge_Backward', 'Idle_A'],
};

/**
 * Skeletons. Their own idle, walk and death read as undead rather than as a
 * person with the flesh removed, and `Skeletons_Awaken_Standing` finally gives
 * the DORMANT -> AGGROED transition a body: a pile of bones standing up.
 *
 * Everything not listed falls through to the humanoid table, because the pack
 * ships no undead crouch, block, parry, cast or dodge and inventing one by
 * substitution would be worse than reusing the humanoid motion.
 */
const MEDIUM_UNDEAD: Partial<IntentTable> = {
  IDLE: ['Skeletons_Idle', 'Idle_A'],
  MOVING: ['Skeletons_Walking', 'Walking_A'],
  // No undead run clip. Falls to the humanoid sprint rather than to the undead
  // walk: a skeleton chasing you at a walking gait while covering ground at
  // 5.6 m/s would read as a bug, and foot-sliding is the more forgivable of
  // the two errors.
  SPRINTING: ['Running_A', 'Skeletons_Walking'],
  DEAD: ['Skeletons_Death', 'Death_A'],
  AWAKEN: ['Skeletons_Awaken_Standing', 'Skeletons_Awaken_Floor', 'Spawn_Ground'],
};

/** The large rig — the Bone Warden alone. A smaller pack: no crouch, no sneak, no magic. */
const LARGE_HUMANOID: IntentTable = {
  IDLE: ['Idle_A'],
  MOVING: ['Walking_A'],
  // The large set ships no backwards walk, and nothing on this rig backs up
  // anyway: monster facing is still derived from travel (`resolveFacing`'s null
  // branch), so the Bone Warden is always walking forward.
  MOVING_BACKWARD: ['Walking_A'],
  SPRINTING: ['Running_A'],
  // A boss does not crouch, and nothing sets CROUCHING on a monster; these
  // exist so the table is total rather than because they will ever be hit.
  CROUCH_IDLE: ['Idle_A'],
  CROUCH_MOVING: ['Walking_A'],
  BLOCKING: ['Melee_Blocking', 'Idle_A'],
  PARRY: ['Melee_Block', 'Melee_Blocking', 'Idle_A'],
  // The large rig ships no jump clip and nothing on it jumps. Idle so the
  // table stays total; `playJump` skips a clip that is just the idle pose.
  JUMP: ['Idle_A'],
  SIDESTEP_LEFT: ['Dodge_Left', 'Dodge_Backwards', 'Idle_A'],
  SIDESTEP_RIGHT: ['Dodge_Right', 'Dodge_Backwards', 'Idle_A'],
  CASTING: ['Idle_A'],
  DEAD: ['Death_A'],
  USE_ITEM: ['Idle_A'],
  INTERACT: ['Idle_A'],
  AWAKEN: ['Idle_A'],
  // Nothing on this rig casts anything: no player uses it and the Bone
  // Warden has no abilities. Filled so the table stays total, with the large
  // set's own spelling of each motion where it has one.
  ABILITY_STRIKE: ['Melee_1H_Slash', 'Idle_A'],
  ABILITY_SWEEP: ['Melee_2H_Attack', 'Melee_1H_Slash', 'Idle_A'],
  ABILITY_SHOUT: ['Melee_Unarmed_Smash', 'Melee_2H_Slam', 'Idle_A'],
  ABILITY_WARD: ['Melee_Block', 'Melee_Blocking', 'Idle_A'],
  ABILITY_CAST: ['Idle_A'],
  ABILITY_STEALTH: ['Dodge_Backwards', 'Idle_A'],
};

/**
 * Attack clips by weapon weight and swing direction.
 *
 * Keyed on `weaponClass` rather than on class or archetype so the animation
 * matches what melee-combat.md is actually simulating — a HEAVY swing's 410ms
 * windup wants a two-handed wind-up, whoever is holding it. Direction comes
 * from the swing the player chose (J/K/L, or LMB's default OVERHEAD).
 */
const MEDIUM_ATTACKS: Record<WeaponClass, Record<string, readonly string[]>> = {
  LIGHT: {
    OVERHEAD: ['Melee_1H_Attack_Chop'],
    LEFT: ['Melee_1H_Attack_Slice_Diagonal'],
    RIGHT: ['Melee_1H_Attack_Slice_Horizontal'],
  },
  MEDIUM: {
    OVERHEAD: ['Melee_1H_Attack_Chop'],
    LEFT: ['Melee_1H_Attack_Slice_Diagonal'],
    RIGHT: ['Melee_1H_Attack_Slice_Horizontal'],
  },
  BLUNT: {
    // A mace is swung, not sliced. Chop for every direction rather than a
    // diagonal draw-cut, which reads as an edge the weapon does not have.
    OVERHEAD: ['Melee_2H_Attack_Chop', 'Melee_1H_Attack_Chop'],
    LEFT: ['Melee_1H_Attack_Chop'],
    RIGHT: ['Melee_1H_Attack_Slice_Horizontal'],
  },
  HEAVY: {
    OVERHEAD: ['Melee_2H_Attack_Chop'],
    LEFT: ['Melee_2H_Attack_Slice'],
    RIGHT: ['Melee_2H_Attack_Spin'],
  },
  REACH: {
    // A polearm thrusts. `Melee_1H_Attack_Stab` leads every direction, with the
    // two-handed chop behind it only so the chain still resolves on a rig that
    // does not ship the stab.
    OVERHEAD: ['Melee_1H_Attack_Stab', 'Melee_2H_Attack_Chop'],
    LEFT: ['Melee_1H_Attack_Stab', 'Melee_2H_Attack_Slice'],
    RIGHT: ['Melee_1H_Attack_Stab', 'Melee_2H_Attack_Slice'],
  },
};

const LARGE_ATTACKS: Record<WeaponClass, Record<string, readonly string[]>> = {
  LIGHT: { OVERHEAD: ['Melee_1H_Slash'], LEFT: ['Melee_1H_Slash'], RIGHT: ['Melee_1H_Stab'] },
  MEDIUM: { OVERHEAD: ['Melee_1H_Slash'], LEFT: ['Melee_1H_Slash'], RIGHT: ['Melee_1H_Stab'] },
  BLUNT: { OVERHEAD: ['Melee_2H_Slam'], LEFT: ['Melee_2H_Slam'], RIGHT: ['Melee_1H_Slash'] },
  HEAVY: { OVERHEAD: ['Melee_2H_Slam'], LEFT: ['Melee_2H_Attack'], RIGHT: ['Melee_2H_Attack'] },
  REACH: { OVERHEAD: ['Melee_1H_Stab'], LEFT: ['Melee_1H_Stab'], RIGHT: ['Melee_1H_Stab'] },
};

/** Last-resort attack motions, appended to every attack chain. */
const ATTACK_FALLBACKS: readonly string[] = [
  'Melee_1H_Attack_Chop', 'Melee_2H_Attack_Chop', 'Melee_1H_Slash', 'Melee_2H_Attack',
  'Melee_Unarmed_Attack_Punch_A', 'Melee_Unarmed_Punch', 'Use_Item', 'Idle_A',
];

/** The two hit-reaction clips, so a repeated stagger does not replay one pose. */
export const STAGGER_CLIPS: Record<RigSize, readonly string[]> = {
  medium: ['Hit_A', 'Hit_B'],
  large: ['Hit_A'],
};

export function clipCandidates(rig: RigSize, style: ClipStyle, intent: AnimIntent): readonly string[] {
  if (rig === 'large') return LARGE_HUMANOID[intent];
  if (style === 'undead') {
    const undead = MEDIUM_UNDEAD[intent];
    // Concatenated, not replaced: an undead chain that runs out should keep
    // falling through the humanoid one rather than dead-ending.
    if (undead) return [...undead, ...MEDIUM_HUMANOID[intent]];
  }
  return MEDIUM_HUMANOID[intent];
}

export function attackCandidates(rig: RigSize, weaponClass: WeaponClass, direction?: string): readonly string[] {
  const table = rig === 'large' ? LARGE_ATTACKS : MEDIUM_ATTACKS;
  const byDirection = table[weaponClass] ?? table.MEDIUM;
  // An unknown or absent direction is an OVERHEAD swing, matching
  // melee-combat.md Core Rule #2's "LMB always swings OVERHEAD" default.
  const chain = byDirection[direction ?? 'OVERHEAD'] ?? byDirection.OVERHEAD;
  return [...chain, ...ATTACK_FALLBACKS];
}

/**
 * The first candidate the rig actually has, or null if none of them exist.
 *
 * Returning null rather than a guessed name so the caller can decide: a
 * missing clip must not silently become "keep the current pose", which is the
 * failure mode that made a casting druid look frozen.
 */
export function resolveClip(candidates: readonly string[], available: ReadonlySet<string>): string | null {
  for (const name of candidates) {
    if (available.has(name)) return name;
  }
  return null;
}
